import { useState } from 'react';

import { ArrowLeftOutline, CloseFill, PlusOutline, SparkleOutline } from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import { Button, IconButton, Stack, Typography } from '@lyearn/ui';
import { aiActionButtonTextStyles } from '@/components/LyearnAI/components/GradientText/styles';
import { SearchBar } from '@/components/SearchBar';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import { useAppConfig } from '@/hooks/useAppConfig';
import { ChannelTypeEnum } from '@/types/schema';

import { useGetAllAiChannelsQuery } from '../../graphql/queries/generated/getAllAIChannels';
import { ViewMode } from '../../types';

const horizontalDivider = css`
  height: 0.1rem;
`;

const MyAIChannels = ({
  onClose,
  setCurrentChannel,
  setViewMode,
}: {
  onClose: () => void;
  setCurrentChannel: React.Dispatch<React.SetStateAction<ChatFragment | null>>;
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>;
}) => {
  const { workspaceId } = useAppConfig();
  const [data] = useGetAllAiChannelsQuery({
    variables: {
      filters: {
        types: [ChannelTypeEnum.ChatWithAi],
        parentId: workspaceId!,
      },
    },
  });

  const [search, setSearch] = useState('');

  return (
    <Stack className={cn('h-[100.8rem] w-full rounded-16')} direction="col">
      <Stack className="overflow-y-auto overflow-x-hidden mx-[1.6rem]" direction="col">
        <Stack direction="col">
          <Stack alignItems="center" className="w-[40.8rem] h-[3.2rem]" justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <SparkleOutline fill="#5a27ee" height={20} width={20} />
              <Typography
                className={cn(aiActionButtonTextStyles)}
                numberOfLines={1}
                variant="heading-xs">
                All Chats
              </Typography>
            </Stack>
            <Stack alignItems="center">
              <Button
                appearance="secondary"
                size="x-small"
                variant="filled"
                onClick={() => setCurrentChannel(null)}>
                <Stack alignItems="center">
                  <PlusOutline height={24} width={24} />
                  <Typography variant="heading-xxs">New</Typography>
                </Stack>
              </Button>
              <IconButton
                Icon={CloseFill}
                appearance="primary"
                shape="square"
                size="xl"
                onClick={onClose}
              />
            </Stack>
          </Stack>
          <div className={cn(horizontalDivider, '-mx-16 mt-12 bg-interactive-secondary-hover')} />
        </Stack>
        <Stack
          alignItems="center"
          className="my-16 cursor-pointer"
          gapX={8}
          onClick={() => setViewMode(ViewMode.Chat)}>
          <ArrowLeftOutline fillColorToken="icon-purple" />
          <Typography
            className="text-text-purple"
            variant="heading-xxs"
            onClick={() => {
              setViewMode(ViewMode.Chat);
              setCurrentChannel(data?.data?.channels?.edges?.[0]?.node || null);
            }}>
            Back to current chat
          </Typography>
        </Stack>
        <Stack className="overflow-y-auto overflow-x-hidden" direction="col">
          <SearchBar className="my-16" shape="square" value={search} onChange={setSearch} />
          {data?.data?.channels?.edges?.map((channel) => (
            <Stack
              key={channel.node.id}
              className="px-12 py-[9px] hover:bg-bg-hover rounded-8 cursor-pointer"
              gapY={2}
              onClick={() => {
                setViewMode(ViewMode.Chat);
                setCurrentChannel(channel.node);
              }}>
              <Typography className="text-text-secondary" variant="label-02">
                {channel.node.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MyAIChannels;
